import { useEffect, useRef, useState } from 'react';
import { DndContext, DragOverlay, type DragEndEvent, type DragOverEvent, type DragStartEvent, PointerSensor, useSensor, useSensors, pointerWithin, rectIntersection, type CollisionDetection } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { createPortal } from 'react-dom';
import type { Column, TicketSummary, User } from '@vd/types';
import { KCard } from '@vd/ui';
import { BoardColumn } from './BoardColumn';
import { cardTime } from '../../lib/When';
import { readLocalThumb } from '../annotations/localThumbs';
import styles from './BoardView.module.css';
import { dropAnchor, type DropAnchor } from './dropAnchor';

export interface BoardViewProps {
  columns: Column[];
  tickets: TicketSummary[];
  creatorsById: Map<string, User>;
  membersById: Map<string, User>;
  showThumbnails: boolean;
  onOpenTicket: (t: TicketSummary) => void;
  onCopyLink: (t: TicketSummary) => void;
  onOpenSource: (t: TicketSummary) => void;
  /**
   * `anchor` is where the card was dropped, as the card beside it: the server
   * also counts the tickets the board hides, so an index among the cards shown
   * would put it somewhere else (see `dropAnchor`).
   */
  onMoveTicket: (ticketId: string, targetColumnId: string, anchor: DropAnchor | undefined) => void;
  /**
   * Multi-select drag handler. Fires when the user drags one of the
   * bulk-selected cards to a different column. All selected ticket
   * ids move to the target column at once. Index isn't passed — bulk
   * moves currently land at the end of the destination column (which
   * matches the bulk-move-modal flow). Cleared in the parent's
   * mutation onSuccess via clearBulk(), so the user doesn't have to
   * un-check the cards manually after the drop.
   */
  onBulkMove?: (ticketIds: string[], targetColumnId: string) => void;
  onStatusClick?: (t: TicketSummary, a: HTMLElement) => void;
  onTypeClick?: (t: TicketSummary, a: HTMLElement) => void;
  onAssignClick?: (t: TicketSummary, a: HTMLElement) => void;
  onOverflowClick?: (t: TicketSummary, a: HTMLElement) => void;
  onAddTicket?: (columnId: string, anchor: HTMLElement) => void;
  onColumnMenu?: (columnId: string, anchor: HTMLElement) => void;
  /** Set of ticket ids currently bulk-selected. Undefined → no bulk mode. */
  bulkSelection?: Set<string>;
  onBulkToggle?: (ticketId: string, next: boolean) => void;
  /** Tickets currently in flight on a bulk operation — render dimmed
   * with a spinner overlay so the user gets immediate feedback. */
  movingTicketIds?: Set<string>;
  /** TOADO-251: project bug-intake toggle, forwarded to columns so the
   *  marked column can render its self-explaining note. */
  intakeEnabled?: boolean;
}

/**
 * Collision detection tuned for the kanban: pointerWithin first so the
 * card directly under the cursor wins, falling back to rectIntersection
 * when the pointer sits in a gap (between cards or in a column's empty
 * area). Default closestCorners/closestCenter both give the wrong over
 * target when the source's slot collapses to a 42px placeholder, because
 * the small placeholder ends up "closer" than the real target.
 */
const kanbanCollision: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) return pointerCollisions;
  return rectIntersection(args);
};

/**
 * The list with the dragged ticket moved into the column of what it is over:
 * at the end of a column it is over, or just before a card in another column.
 * The same list back when it is already in that column, or over nothing known.
 */
export function moveIntoColumnOf(list: TicketSummary[], activeTicketId: string, overId: string, columns: Column[]): TicketSummary[] {
  const active = list.find((t) => t.id === activeTicketId);
  if (!active) return list;
  const overColumn = columns.find((c) => c.id === overId);
  if (overColumn) {
    // Hovering over a column container (empty area below the last card, or
    // an empty column). Place at the END of the target column so the visual
    // result matches dropping on a card's lower edge.
    if (active.columnId === overColumn.id) return list;
    const next = list.map((t) => (t.id === activeTicketId ? { ...t, columnId: overColumn.id } : t));
    const activeIdx = next.findIndex((t) => t.id === activeTicketId);
    let lastIdx = -1;
    for (let i = 0; i < next.length; i++) {
      if (next[i]!.columnId === overColumn.id && next[i]!.id !== activeTicketId) lastIdx = i;
    }
    if (lastIdx === -1) return next; // target column is empty — leave active where it is
    // arrayMove(_, from, to) inserts AT `to` after splicing out `from`.
    // For forward (activeIdx < lastIdx) the splice shifts lastIdx down
    // by 1, so target=lastIdx lands AT the end. For backward we need
    // to insert just past lastIdx.
    return arrayMove(next, activeIdx, activeIdx < lastIdx ? lastIdx : lastIdx + 1);
  }
  const overTicket = list.find((t) => t.id === overId);
  if (!overTicket || overTicket.columnId === active.columnId) return list;
  // Cross-column: re-home the active ticket to the over ticket's column
  // and place it BEFORE the over ticket. arrayMove inserts AT the target
  // index after splicing — for forward moves that effectively lands the
  // item AFTER the destination, so we subtract 1 to compensate.
  const next = list.map((t) => (t.id === activeTicketId ? { ...t, columnId: overTicket.columnId } : t));
  const activeIdx = next.findIndex((t) => t.id === activeTicketId);
  const overIdx = next.findIndex((t) => t.id === overTicket.id);
  return arrayMove(next, activeIdx, activeIdx < overIdx ? overIdx - 1 : overIdx);
}

/**
 * The list after one hover, as a drag shows it: into the column of what the
 * dragged ticket is over (`moveIntoColumnOf`), or, over another card in its
 * own column, moved to that card's place. arrayMove's standard sortable
 * behavior matches the user's intent (drag past = swap position) without
 * adjustment.
 */
export function applyHover(list: TicketSummary[], activeTicketId: string, overId: string, columns: Column[]): TicketSummary[] {
  const moved = moveIntoColumnOf(list, activeTicketId, overId, columns);
  if (moved !== list) return moved;
  const activeIdx = list.findIndex((t) => t.id === activeTicketId);
  const overIdx = list.findIndex((t) => t.id === overId);
  if (activeIdx === -1 || overIdx === -1 || activeIdx === overIdx) return list;
  return arrayMove(list, activeIdx, overIdx);
}

/**
 * The list as it is where a dragged card was let go. A hover still held when
 * it was let go is played first, once, so the card is where the pointer last
 * put it in its column (z.ai, round 2); then it goes into the column of what
 * the pointer is over, if it is not there yet (round 1). The final hover is not
 * played again: it has been, and a second arrayMove would carry the card one
 * place past where it was dropped.
 *
 * `echoFrom` is the column the card has just left, while a hover back into it
 * is taken for the layout's echo of that move (see the hold in `BoardView`).
 * Such a hover is not played, and a release over that column keeps the card in
 * the column it is drawn in: the drop is what the person was looking at (z.ai,
 * round 3). A real move back draws the card there and ends the echo.
 */
export function releasedList(
  list: TicketSummary[], activeTicketId: string, heldOverId: string | null, overId: string, columns: Column[],
  echoFrom: string | null = null,
): TicketSummary[] {
  const columnOf = (id: string) => columns.find((c) => c.id === id)?.id ?? list.find((t) => t.id === id)?.columnId;
  const echo = (id: string) => echoFrom !== null && columnOf(id) === echoFrom;
  const settled = heldOverId && !echo(heldOverId) ? applyHover(list, activeTicketId, heldOverId, columns) : list;
  return echo(overId) ? settled : moveIntoColumnOf(settled, activeTicketId, overId, columns);
}

export function BoardView({ columns, tickets, creatorsById, membersById, showThumbnails, onMoveTicket, onBulkMove, bulkSelection, movingTicketIds, intakeEnabled, ...handlers }: BoardViewProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Local copy of tickets so we can optimistically reorder / reassign columns during a drag.
  const [localTickets, setLocalTickets] = useState<TicketSummary[]>(tickets);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Reset local state only when the tickets PROP itself changes (server
  // re-fetch, mutation, etc). Previous version reset whenever `activeId`
  // flipped, which fired on every drag-end and wiped the user's reorder
  // — drop target visible during drag, card snapped back on release.
  //
  // Not while a card is being dragged. The drop is worked out from the order
  // the person is looking at, and a refetch or live update landing mid-drag
  // put the server's order back underneath it, so the drop named the wrong
  // neighbour, or looked like no move at all. The newest list is taken once
  // the drag is over, through `activeId` below.
  const prevTicketsRef = useRef(tickets);
  useEffect(() => {
    if (activeId !== null) return;
    if (prevTicketsRef.current !== tickets) {
      setLocalTickets(tickets);
      prevTicketsRef.current = tickets;
    }
  }, [tickets, activeId]);

  /**
   * Held while a move to another column has not been drawn yet (t-sWrRBH). The
   * move shifts the columns' layout, which can put the pointer back over the
   * column the card just left; moving it back shifts the layout again, and a
   * fast drag across several columns ran that to and fro until React gave up
   * with "Maximum update depth exceeded" and the board crashed. So one move to
   * another column is drawn before the next is made: two frames, the first for
   * React to commit it. A hover that arrives meanwhile is kept, the latest only,
   * and played once the hold ends, never dropped: dnd-kit reports a hover only
   * when what is under the pointer changes, so a dropped one was not reported
   * again, and the card stayed a column behind the pointer (z.ai, round 1).
   * Except a hover back over the column the card has just left: that is the
   * layout's echo of the move, and playing it would carry the to and fro on, a
   * frame pair at a time, for as long as the pointer rests there (z.ai, round
   * 2). If the person did go back, the release puts the card where they let go.
   */
  const holding = useRef(false);
  const heldOver = useRef<DragOverEvent | null>(null);
  const heldFrom = useRef<string | null>(null);
  /** The column a refused echo pointed back into, until a hover is drawn again. */
  const echoed = useRef<string | null>(null);
  const holdFrame = useRef<number | null>(null);
  const replayRef = useRef<(held: DragOverEvent, from: string | null) => void>(() => undefined);
  const endHold = () => {
    if (holdFrame.current !== null) cancelAnimationFrame(holdFrame.current);
    holdFrame.current = null;
    holding.current = false;
    heldOver.current = null;
    heldFrom.current = null;
    echoed.current = null;
  };
  const holdUntilDrawn = (from: string) => {
    holding.current = true;
    heldFrom.current = from;
    holdFrame.current = requestAnimationFrame(() => {
      holdFrame.current = requestAnimationFrame(() => {
        holdFrame.current = null;
        holding.current = false;
        const held = heldOver.current;
        const left = heldFrom.current;
        heldOver.current = null;
        heldFrom.current = null;
        if (held) replayRef.current(held, left);
      });
    });
  };
  useEffect(() => endHold, []);

  const findTicket = (id: string) => localTickets.find((t) => t.id === id);
  const findColumn = (id: string) => columns.find((c) => c.id === id) ?? null;
  const activeTicket = activeId ? findTicket(activeId) : null;

  const onDragStart = (e: DragStartEvent) => {
    endHold();
    setActiveId(String(e.active.id));
  };

  const onDragOver = (e: DragOverEvent) => {
    const activeTicketId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || activeTicketId === overId) return;

    if (holding.current) { heldOver.current = e; return; }

    const active = findTicket(activeTicketId);
    if (!active) return;

    const overColumnId = findColumn(overId)?.id ?? findTicket(overId)?.columnId;
    if (overColumnId !== undefined && overColumnId !== active.columnId) holdUntilDrawn(active.columnId);
    echoed.current = null;
    setLocalTickets((list) => applyHover(list, activeTicketId, overId, columns));
  };

  // Set while rendering, not in an effect: the hold's frame can fire before a
  // passive effect has run, and would then replay against the last render's list.
  replayRef.current = (held, from) => {
    const overId = held.over ? String(held.over.id) : null;
    if (!overId) return;
    // The echo of the move just drawn: see the hold's comment.
    if (from !== null && (findColumn(overId)?.id ?? findTicket(overId)?.columnId) === from) { echoed.current = from; return; }
    onDragOver(held);
  };

  const onDragEnd = (e: DragEndEvent) => {
    const activeTicketId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;

    const held = heldOver.current;
    const echoFrom = heldFrom.current ?? echoed.current;
    endHold();
    setActiveId(null);

    if (!overId) { setLocalTickets(tickets); return; }

    const released = releasedList(localTickets, activeTicketId, held?.over ? String(held.over.id) : null, overId, columns, echoFrom);
    if (released !== localTickets) setLocalTickets(released);
    const active = released.find((t) => t.id === activeTicketId);
    if (!active) { setLocalTickets(tickets); return; }

    // The ticket's column and place in it after the drop, to tell whether it moved at all.
    const targetColumnId = active.columnId;
    const inColumn = released.filter((t) => t.columnId === targetColumnId);
    const targetIndex = Math.max(0, inColumn.findIndex((t) => t.id === activeTicketId));

    const original = tickets.find((t) => t.id === activeTicketId);
    if (!original) return;
    const columnChanged = original.columnId !== targetColumnId;
    const originalIndex = tickets.filter((t) => t.columnId === original.columnId).findIndex((t) => t.id === activeTicketId);
    const indexChanged = !columnChanged && originalIndex !== targetIndex;

    // Multi-drag: if the user dragged a card that's part of a current
    // bulk selection (>1 ticket), move ALL selected tickets to the
    // target column. A precise per-ticket index isn't possible with
    // the existing bulk-move API (it appends to the destination
    // column), so revert the optimistic reorder and let the bulk-move
    // mutation drive the final state.
    const isMultiDrag = !!bulkSelection && bulkSelection.has(activeTicketId) && bulkSelection.size > 1;
    if (isMultiDrag && columnChanged && onBulkMove) {
      setLocalTickets(tickets);
      onBulkMove(Array.from(bulkSelection), targetColumnId);
      return;
    }

    if (columnChanged || indexChanged) {
      // The move's own update of the list follows, placed where the card was
      // dropped. Without this, a refresh held off during the drag would be
      // applied as the drag ends and flip the card back for a moment before
      // that update arrived.
      prevTicketsRef.current = tickets;
      onMoveTicket(activeTicketId, targetColumnId, dropAnchor(inColumn, activeTicketId));
    }
  };

  const onDragCancel = () => {
    endHold();
    setActiveId(null);
    // Cancel (Esc) reverts to the pre-drag server state; user opted out.
    setLocalTickets(tickets);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={kanbanCollision}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div className={styles.board}>
        {columns.map((c, index) => (
          <BoardColumn
            key={c.id}
            tourAnchored={index === 0}
            column={c}
            tickets={localTickets.filter((t) => t.columnId === c.id)}
            creatorsById={creatorsById}
            membersById={membersById}
            showThumbnails={showThumbnails}
            activeId={activeId}
            movingTicketIds={movingTicketIds}
            bulkSelection={bulkSelection}
            intakeEnabled={intakeEnabled}
            {...handlers}
          />
        ))}
      </div>

      {createPortal(
        <DragOverlay dropAnimation={{ duration: 160, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
          {activeTicket ? (
            <div className={styles.overlayCard}>
              <KCard
                ticket={activeTicket}
                creator={creatorsById.get(activeTicket.createdBy)}
                assignees={activeTicket.assignees.map((id) => membersById.get(id)).filter(Boolean) as User[]}
                column={columns.find((c) => c.id === activeTicket.columnId)}
                showThumbnail={showThumbnails}
                thumbnailUrl={activeTicket.thumbnailUrl ?? readLocalThumb(activeTicket.id)}
                formatTime={cardTime}
              />
            </div>
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}
